import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import {
  groupInput,
  input,
  InputProvider,
  nonEmptyValidator,
} from "@redotech/ui/form";
import { optionalEmailValidator } from "@redotech/web-util/form";
import classNames from "classnames";
import { FormEvent, memo, useState } from "react";
import { RedoButton } from "../../arbiter-components/buttons/redo-button";
import { RedoTextInput } from "../../arbiter-components/input/redo-text-input";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { HandleOrderLookupFn } from "../common";
import * as css from "./order-lookup-tool-message.module.css";

const orderLookupForm = groupInput({
  orderEmail: input<string>({
    validator: (value: string) => [
      ...optionalEmailValidator(value),
      ...nonEmptyValidator(value),
    ],
  }),
  orderId: input<string>({
    validator: (value: string) => [...nonEmptyValidator(value)],
  }),
});

type OrderLookupValue = InputProvider.Value<typeof orderLookupForm>;

export const OrderLookupToolMessage = memo(function OrderLookupToolMessage({
  lookupInfo,
  readOnly,
  onOrderLookupRequest,
  conversationId,
  toolMessageId,
  buttonText,
  messageSide = "left",
}: {
  lookupInfo?:
    | { orderEmail?: string; orderId: undefined }
    | { orderEmail?: string; orderId: string };
  readOnly: boolean;
  onOrderLookupRequest?: HandleOrderLookupFn | undefined;
  conversationId: string;
  toolMessageId: string;
  buttonText: string;
  messageSide?: "left" | "right";
}) {
  const [error, setError] = useState<string | undefined>(undefined);

  const initialValue: OrderLookupValue = {
    orderEmail: lookupInfo?.orderEmail ?? "",
    orderId: lookupInfo?.orderId ?? "",
  };
  const input = useInput(orderLookupForm, initialValue);

  const [submitLoad, doSubmitLoad] = useTriggerLoad(async () => {
    if (onOrderLookupRequest) {
      try {
        await onOrderLookupRequest({
          conversationId,
          toolMessageId,
          orderEmail: input.value.orderEmail,
          orderId: input.value.orderId,
        });
        await onOrderLookupRequest({
          conversationId,
          toolMessageId,
          orderEmail: input.value.orderEmail,
          orderId: input.value.orderId,
        });
      } catch (error) {
        if (error instanceof Error) {
          setError(error.message);
        } else {
          setError("An unknown error occurred.");
        }
      }
    }
  });

  const onFormSubmit = useHandler((event?: FormEvent<HTMLElement>) => {
    if (input.allErrors.length > 0) {
      return;
    }

    event?.preventDefault();
    doSubmitLoad();
  });

  return (
    <Flex
      as="form"
      className={classNames(css.orderLookupBubbleContainer, {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        [css.messageSideLeft]: messageSide === "left",
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        [css.messageSideRight]: messageSide === "right",
      })}
      dir="column"
      gap="md"
      onSubmit={onFormSubmit}
      px="lg"
      py="md"
    >
      <RedoTextInput
        label="Email"
        setValue={input.inputs.orderEmail.setValue}
        state={
          readOnly || submitLoad.pending || !onOrderLookupRequest
            ? "disabled"
            : undefined
        }
        value={input.inputs.orderEmail.value}
      />
      <RedoTextInput
        label="Order number"
        setValue={input.inputs.orderId.setValue}
        state={
          readOnly || submitLoad.pending || !onOrderLookupRequest
            ? "disabled"
            : undefined
        }
        value={input.inputs.orderId.value}
      />
      {error && !submitLoad.pending && (
        <Text fontSize="sm" textColor="error">
          {error}
        </Text>
      )}
      <RedoButton
        className={css.submitButton}
        disabled={
          readOnly ||
          submitLoad.pending ||
          !onOrderLookupRequest ||
          input.allErrors.length > 0
        }
        hierarchy="primary"
        onClick={() => onFormSubmit()}
        size="sm"
        text={buttonText}
      />
    </Flex>
  );
});
